import { redirect, replace, unstable_createContext } from "react-router";
import { RestAPI } from "@/utils/api-rest";

import { ErrorElement } from "@/components/Utils/ErrorBoundaryRoute";
import LoadingDialog from "@/components/Utils/LoadingDialog";

import PolicyAndTerms from "@/pages/PolicyAndTerms/PolicyAndTerms";

import { Layout } from "@/pages/layout";
import { FarmComponent } from "@/pages/index";

export const RouterContext = unstable_createContext();

/**
 * @param history {ReturnType<import("react-router").UNSAFE_createBrowserHistory>}
 * @param depsPreload {Array<function(): Promise<any>>}
 * @returns {import("react-router").DataRouteObject[]}
 * */
export default (history, depsPreload) => {
    const personalInfoRef = { current: null };

    return [{
        path: "/",
        element: <Layout history={history} />,
        hydrateFallbackElement: <LoadingDialog />,
        // https://reactrouter.com/en/main/hooks/use-route-error
        errorElement: <ErrorElement name="RootRouteError" />,
        id: "root",
        handle: {
            wiseWait: true,
        },
        loader: async ({ context }) => {
            // preload deps, but dont wait for them
            Promise.all(depsPreload.map(d => d())).then(() => console.debug("Deps Preloaded")).catch(e => console.error(e));

            const originalUrl = globalThis.sessionStorage.getItem("loginUrl");

            // if we do have a saved url from before the external login started, redirect to it
            if (originalUrl) {
                globalThis.sessionStorage.removeItem("loginUrl"); // clean

                const urlObject = new URL(originalUrl);
                // const path = stripBasename(urlObject.pathname, appBasename || "");
                /*if (path) */return replace(urlObject.pathname + urlObject.search + urlObject.hash);
            }

            const cache = personalInfoRef.current;
            delete personalInfoRef.current; // we are updating the info just because an action was called

            const resp = cache ?? await RestAPI.get("apiAdminDropControl", "/personal");
            context.set(RouterContext, resp);
            return Response.json(resp);
        },
        action: async ({ request }) => {
            // all we are doing in this action, is save the modified data on a cache, so the next loader execution will get it
            return Response.json(personalInfoRef.current = await request.json());
        },
        shouldRevalidate: ({ formAction, formMethod, currentUrl, nextUrl }) => {
            if (currentUrl.pathname.includes("/tos") && nextUrl.pathname.includes("personal")) {
                return true; // forced validate after ToS
            }
            return formAction === "/" && formMethod === "PATCH"; // this should only be revalidated on forced revalidations and the PATCH action
        },
        children: [
            {
                path: "tos",
                element: <PolicyAndTerms />,
                loader: async ({ context }) => {
                    if (context.get(RouterContext).politicas_aceptadas) {
                        return redirect("/personal");
                    }
                    return Response.json({});
                },
            },
            {
                element: <FarmComponent history={history} />,
                hydrateFallbackElement: <LoadingDialog />,
                errorElement: <ErrorElement name="TOSRouteError" />,
                id: "with-tos",
                handle: {
                    wiseWait: true, // so we do the redirect to '/tos' before executing other loaders
                },
                loader: async ({ context, request }) => {
                    // https://github.com/remix-run/react-router/issues/11129
                    if (!context.get(RouterContext).politicas_aceptadas) {
                        return redirect("/tos");
                    }

                    const farm = localStorage.getItem("farm_id_selected");
                    const url = new URL(request.url);
                    if (!url.search && farm && url.pathname === "/") {
                        try {
                            const metadata = await RestAPI.get("apiAdminDropControl", `/farms/${farm}/metadata`);
                            if (metadata) {
                                return redirect(`/farms?farm=${farm}`);
                            }
                        } catch {
                            console.log("No access to previous farm");
                            localStorage.removeItem("farm_id_selected");
                        }
                    }
                    return Response.json({});
                },
                children: [
                    {
                        element: null,
                        id: "dummy",
                        index: true
                    },
                ]
            },
        ]
    }];
};
